import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  CheckSquare, 
  Plus, 
  Edit3, 
  Trash2, 
  FileText, 
  MessageSquare, 
  Calendar,
  User,
  BarChart3,
  Target,
  Clock,
  CheckCircle2
} from 'lucide-react';


const DigitalChecklists = ({ projectId, user }) => {
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvingChecklist, setApprovingChecklist] = useState(null);
  const [newChecklistForm, setNewChecklistForm] = useState({
    title: '',
    description: '',
    template_type: 'custom',
    deliverable_id: '',
    items: []
  });
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    checklist_items: [],
    overall_status: 'pending'
  });

  useEffect(() => {
    fetchChecklists();
    fetchTemplates();
  }, [projectId]);

  const fetchChecklists = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/digital-checklists`);
      setChecklists(response.data);
      if (response.data.length > 0 && !selectedChecklist) {
        setSelectedChecklist(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching checklists:', error);
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/checklist-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreateChecklist = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/projects/${projectId}/digital-checklists`,
        newChecklistForm
      );
      setChecklists([...checklists, response.data]);
      setCreateDialogOpen(false);
      setNewChecklistForm({
        title: '',
        description: '',
        template_type: 'custom',
        deliverable_id: '',
        items: []
      });
      toast.success('Digital checklist created successfully');
    } catch (error) {
      console.error('Error creating checklist:', error);
      toast.error('Failed to create checklist');
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setNewChecklistForm(prev => ({
        ...prev,
        template_type: templateId,
        title: template.name,
        description: template.description,
        items: template.items.map(item => ({
          id: Math.random().toString(36).substr(2, 9),
          text: item.text,
          required: item.required,
          completed: false,
          comments: ''
        }))
      }));
    }
  };

  const handleUpdateChecklistItem = async (checklistId, itemId, updates) => {
    try {
      const checklist = checklists.find(c => c.id === checklistId);
      const updatedItems = checklist.items.map(item => 
        item.id === itemId 
          ? { ...item, ...updates, updated_at: new Date().toISOString() }
          : item
      );

      const response = await axios.patch(
        `${BACKEND_URL}/api/projects/${projectId}/digital-checklists/${checklistId}`,
        { items: updatedItems }
      );

      setChecklists(checklists.map(c => 
        c.id === checklistId ? response.data : c
      ));

      if (selectedChecklist && selectedChecklist.id === checklistId) {
        setSelectedChecklist(response.data);
      }

      toast.success('Checklist updated successfully');
    } catch (error) {
      console.error('Error updating checklist:', error);
      toast.error('Failed to update checklist');
    }
  };

  const handleEditChecklist = (checklist) => {
    setEditingChecklist(checklist);
    setEditForm({
      title: checklist.title || '',
      description: checklist.description || '',
      checklist_items: checklist.items || checklist.checklist_items || [],
      overall_status: checklist.overall_status || 'pending'
    });
    setEditDialogOpen(true);
  };

  const handleUpdateChecklist = async () => {
    try {
      const updateData = {
        title: editForm.title,
        description: editForm.description,
        items: editForm.checklist_items,
        overall_status: editForm.overall_status
      };

      await axios.patch(
        `${BACKEND_URL}/api/projects/${projectId}/digital-checklists/${editingChecklist.id}`,
        updateData
      );

      const statusMsg = user.role === 'social_actor' ? 
        'Checklist edit submitted for approval!' : 
        'Checklist updated successfully!';
      toast.success(statusMsg);

      setEditDialogOpen(false);
      setEditingChecklist(null);
      fetchChecklists();
    } catch (error) {
      console.error('Error updating checklist:', error);
      toast.error(error.response?.data?.detail || 'Failed to update checklist');
    }
  };

  const handleOpenApprovalDialog = (checklist) => {
    setApprovingChecklist(checklist);
    setApprovalDialogOpen(true);
  };

  const handleApproveEdit = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/projects/${projectId}/digital-checklists/${approvingChecklist.id}/approve-edit`,
        { approved: true }
      );
      
      toast.success('Checklist edit approved successfully!');
      setApprovalDialogOpen(false);
      setApprovingChecklist(null);
      fetchChecklists();
    } catch (error) {
      console.error('Error approving checklist:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve checklist edit');
    }
  };

  const handleRejectEdit = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/projects/${projectId}/digital-checklists/${approvingChecklist.id}/reject-edit`,
        { rejected: true }
      );
      
      toast.success('Checklist edit rejected');
      setApprovalDialogOpen(false);
      setApprovingChecklist(null);
      fetchChecklists();
    } catch (error) {
      console.error('Error rejecting checklist:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject checklist edit');
    }
  };

  const handleDeleteChecklist = async (checklistId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}/digital-checklists/${checklistId}`);
      setChecklists(checklists.filter(c => c.id !== checklistId));
      if (selectedChecklist && selectedChecklist.id === checklistId) {
        setSelectedChecklist(checklists[0] || null);
      }
      toast.success('Checklist deleted successfully');
    } catch (error) {
      console.error('Error deleting checklist:', error);
      toast.error('Failed to delete checklist');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Digital Checklists</h2>
          <p className="text-gray-600">Manage deliverable checklists and track progress</p>
        </div>
        {user.role !== 'investor' && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Create Checklist</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Digital Checklist</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={newChecklistForm.title}
                    onChange={(e) => setNewChecklistForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter checklist title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newChecklistForm.description}
                    onChange={(e) => setNewChecklistForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter checklist description"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template">Template</Label>
                  <Select onValueChange={handleTemplateSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom Checklist</SelectItem>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateChecklist} disabled={!newChecklistForm.title}>
                    Create Checklist
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Checklists</p>
                <p className="text-2xl font-bold text-gray-900">{checklists.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-700" />
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {checklists.filter(c => c.completion_percentage === 100).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-orange-700" />
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">
                  {checklists.filter(c => c.completion_percentage > 0 && c.completion_percentage < 100).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Progress</p>
                <p className="text-2xl font-bold text-gray-900">
                  {checklists.length > 0 
                    ? Math.round(checklists.reduce((sum, c) => sum + c.completion_percentage, 0) / checklists.length)
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Checklists List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Checklists</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-2 p-4">
                {checklists.map(checklist => (
                  <div
                    key={checklist.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedChecklist?.id === checklist.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedChecklist(checklist)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900 truncate">{checklist.title}</h4>
                      <div className="flex gap-1">
                        {checklist.edit_status === 'pending_approval' && (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 mr-1">
                            Edit Pending
                          </Badge>
                        )}
                        <Badge className={getStatusColor(checklist.status)}>
                          {checklist.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{checklist.description}</p>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Progress</span>
                        <span className="text-xs font-medium text-gray-700">
                          {checklist.completion_percentage}%
                        </span>
                      </div>
                      <Progress value={checklist.completion_percentage} className="h-2" />
                    </div>

                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-gray-500">
                        {checklist.items?.length || 0} items
                      </span>
                      {user.role !== 'investor' && (
                        <div className="flex gap-1">
                          {checklist.edit_status === 'pending_approval' && 
                           (user.role === 'project_manager' || user.role === 'administrator') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenApprovalDialog(checklist);
                              }}
                              className="text-green-700 hover:text-green-800"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Review
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditChecklist(checklist);
                            }}
                            className="text-blue-700 hover:text-blue-800"
                          >
                            <Edit3 className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteChecklist(checklist.id);
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {checklists.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CheckSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No checklists created yet</p>
                    <p className="text-sm">Create your first checklist to get started</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Checklist Details */}
        <div className="lg:col-span-2">
          {selectedChecklist ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <Target className="h-5 w-5" />
                      <span>{selectedChecklist.title}</span>
                    </CardTitle>
                    <p className="text-gray-600 mt-1">{selectedChecklist.description}</p>
                  </div>
                  <Badge className={getStatusColor(selectedChecklist.status)}>
                    {selectedChecklist.completion_percentage}% Complete
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                      <span className="text-sm text-gray-600">
                        {selectedChecklist.items?.filter(item => item.completed).length || 0} of {selectedChecklist.items?.length || 0} completed
                      </span>
                    </div>
                    <Progress value={selectedChecklist.completion_percentage} className="h-3" />
                  </div>

                  {/* Checklist Items */}
                  <div className="space-y-3">
                    {selectedChecklist.items?.map(item => (
                      <div key={item.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            checked={item.completed}
                            onCheckedChange={(checked) => {
                              if (user.role !== 'investor') {
                                handleUpdateChecklistItem(selectedChecklist.id, item.id, {
                                  completed: checked,
                                  completed_by: checked ? user.id : null,
                                  completed_at: checked ? new Date().toISOString() : null
                                });
                              }
                            }}
                            disabled={user.role === 'investor'}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center space-x-2">
                              <p className={`font-medium ${item.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                {item.text}
                              </p>
                              {item.required && (
                                <Badge variant="outline" className="text-xs">Required</Badge>
                              )}
                            </div>
                            
                            {item.comments && (
                              <div className="bg-gray-50 p-2 rounded text-sm text-gray-700">
                                <MessageSquare className="h-3 w-3 inline mr-1" />
                                {item.comments}
                              </div>
                            )}

                            {item.completed && item.completed_at && (
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <div className="flex items-center space-x-1">
                                  <Calendar className="h-3 w-3" />
                                  <span>{new Date(item.completed_at).toLocaleDateString('en-GB')}</span>
                                </div>
                                {item.completed_by && (
                                  <div className="flex items-center space-x-1">
                                    <User className="h-3 w-3" />
                                    <span>Completed by user</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {user.role !== 'investor' && (
                              <div className="space-y-2">
                                <Textarea
                                  placeholder="Add comments..."
                                  value={item.comments || ''}
                                  onChange={(e) => {
                                    handleUpdateChecklistItem(selectedChecklist.id, item.id, {
                                      comments: e.target.value
                                    });
                                  }}
                                  className="text-sm"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {(!selectedChecklist.items || selectedChecklist.items.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <CheckSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No checklist items yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <CheckSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Select a checklist to view details</p>
                <p>Choose a checklist from the left panel to see its items and progress</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Checklist Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Checklist</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter checklist title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter checklist description"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-status">Overall Status</Label>
              <Select
                value={editForm.overall_status}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, overall_status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Checklist Items</Label>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                {editForm.checklist_items && editForm.checklist_items.length > 0 ? (
                  editForm.checklist_items.map((item, index) => (
                    <div key={item.id || index} className="flex items-start space-x-2 p-2 bg-gray-50 rounded">
                      <Checkbox
                        checked={item.completed || false}
                        onCheckedChange={(checked) => {
                          const updatedItems = [...editForm.checklist_items];
                          updatedItems[index] = { ...item, completed: checked };
                          setEditForm(prev => ({ ...prev, checklist_items: updatedItems }));
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className={`text-sm ${item.completed ? 'line-through text-gray-500' : ''}`}>
                          {item.text}
                        </p>
                        {item.required && (
                          <Badge variant="outline" className="text-xs mt-1">Required</Badge>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No checklist items</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingChecklist(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateChecklist}
                disabled={!editForm.title}
              >
                {user.role === 'social_actor' ? 'Submit for Approval' : 'Update Checklist'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Review Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Checklist Edit</DialogTitle>
          </DialogHeader>
          {approvingChecklist && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-1">{approvingChecklist.title}</h3>
                <p className="text-sm text-gray-600">{approvingChecklist.description}</p>
              </div>

              {approvingChecklist.pending_changes && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Proposed Changes:</h4>
                  <div className="bg-white border rounded-lg divide-y">
                    {Object.entries(approvingChecklist.pending_changes).map(([field, newValue]) => {
                      // Skip internal fields
                      if (field === 'updated_at' || field === 'completion_percentage') return null;
                      
                      const oldValue = approvingChecklist[field];
                      const fieldLabels = {
                        title: 'Title',
                        description: 'Description',
                        overall_status: 'Overall Status',
                        items: 'Checklist Items'
                      };
                      
                      const label = fieldLabels[field] || field;

                      // Handle items array comparison
                      if (field === 'items') {
                        return (
                          <div key={field} className="p-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-2">Current Items:</p>
                                <div className="bg-red-50 p-3 rounded space-y-1 max-h-40 overflow-y-auto">
                                  {Array.isArray(oldValue) && oldValue.length > 0 ? (
                                    oldValue.map((item, idx) => (
                                      <p key={idx} className="text-sm text-gray-700">
                                        {item.completed ? '✓' : '○'} {item.text}
                                      </p>
                                    ))
                                  ) : (
                                    <p className="text-sm text-gray-500 italic">No items</p>
                                  )}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-2">New Items:</p>
                                <div className="bg-green-50 p-3 rounded space-y-1 max-h-40 overflow-y-auto">
                                  {Array.isArray(newValue) && newValue.length > 0 ? (
                                    newValue.map((item, idx) => (
                                      <p key={idx} className="text-sm text-gray-700">
                                        {item.completed ? '✓' : '○'} {item.text}
                                      </p>
                                    ))
                                  ) : (
                                    <p className="text-sm text-gray-500 italic">No items</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Handle other fields
                      if (String(oldValue) !== String(newValue)) {
                        return (
                          <div key={field} className="p-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Current:</p>
                                <p className="text-sm bg-red-50 p-2 rounded text-gray-700">
                                  {String(oldValue) || <span className="italic text-gray-500">Empty</span>}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">New:</p>
                                <p className="text-sm bg-green-50 p-2 rounded text-gray-700">
                                  {String(newValue) || <span className="italic text-gray-500">Empty</span>}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setApprovalDialogOpen(false);
                    setApprovingChecklist(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRejectEdit}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Reject Changes
                </Button>
                <Button
                  onClick={handleApproveEdit}
                  className="bg-green-700 hover:bg-green-800"
                >
                  Approve Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DigitalChecklists;